import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Package, Loader2, AlertCircle, FileCheck, Ban
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { returnService } from '../../../../services/erpApi';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import './ReturnsDetailPage.css';

interface ReturnDetail {
  id: number;
  type: 'exchange' | 'return';
  customer_id: number;
  customer_name?: string;
  status: 'pending' | 'collected' | 'in_inspection' | 'approved' | 'rejected' | 'cancelled';
  total_amount: number;
  observation?: string;
  created_at: string;
  approved_at?: string;
  approved_by?: number;
  items: Array<{
    id: number;
    product_id: number;
    product_name?: string;
    quantity: number;
    unit_price: number;
    reason?: string;
  }>;
}

const ReturnsDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [returnDetail, setReturnDetail] = useState<ReturnDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canApprove = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_APPROVE);

  useEffect(() => {
    const fetchReturn = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);
      try {
        const response = await returnService.getById(parseInt(id));
        if (response.success) {
          setReturnDetail(response.data);
        } else {
          setError(response.message || 'Erro ao carregar devolução');
        }
      } catch (error: any) {
        console.error('Erro ao carregar devolução:', error);
        setError(error.response?.data?.message || 'Erro ao carregar devolução');
      } finally {
        setLoading(false);
      }
    };

    fetchReturn();
  }, [id]);

  const handleApproveClick = () => {
    if (!id || !canApprove) return;
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!id) return;
    
    setShowApproveModal(false);

    try {
      const response = await returnService.approve(parseInt(id));
      if (response.success) {
        // Recarregar dados
        const refreshResponse = await returnService.getById(parseInt(id));
        if (refreshResponse.success) {
          setReturnDetail(refreshResponse.data);
        }
      } else {
        alert(response.message || 'Erro ao aprovar devolução');
      }
    } catch (error: any) {
      console.error('Erro ao aprovar devolução:', error);
      alert(error.response?.data?.message || 'Erro ao aprovar devolução');
    }
  };

  const handleRejectClick = () => {
    if (!id || !canApprove) return;
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!id || !rejectReason.trim()) {
      alert('Por favor, informe o motivo da rejeição.');
      return;
    }
    
    setShowRejectModal(false);

    try {
      const response = await returnService.reject(parseInt(id), rejectReason.trim());
      if (response.success) {
        // Recarregar dados
        const refreshResponse = await returnService.getById(parseInt(id));
        if (refreshResponse.success) {
          setReturnDetail(refreshResponse.data);
        }
      } else {
        alert(response.message || 'Erro ao rejeitar devolução');
      }
    } catch (error: any) {
      console.error('Erro ao rejeitar devolução:', error);
      alert(error.response?.data?.message || 'Erro ao rejeitar devolução');
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Aprovada';
      case 'pending': return 'Aguardando';
      case 'collected': return 'Coletada';
      case 'in_inspection': return 'Em Inspeção';
      case 'rejected': return 'Rejeitada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="returns-detail-page">
        <div className="loading-container">
          <Loader2 className="animate-spin text-indigo-600" size={48} />
          <p className="text-slate-500 mt-4">Carregando devolução...</p>
        </div>
      </div>
    );
  }

  if (error || !returnDetail) {
    return (
      <div className="returns-detail-page">
        <div className="error-container">
          <AlertCircle size={48} className="text-rose-500" />
          <h2>Erro ao carregar devolução</h2>
          <p>{error || 'Devolução não encontrada'}</p>
          <button 
            onClick={() => navigate('/erp/estoque/trocas')}
            className="btn-primary mt-4"
          >
            Voltar para Lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="returns-detail-page">
      <div className="detail-header">
        <button 
          onClick={() => navigate('/erp/estoque/trocas')}
        >
          <ArrowLeft size={16} /> Voltar para Lista
        </button>
        <span className={`status-badge ${returnDetail.status}`}>
          {getStatusLabel(returnDetail.status)}
        </span>
      </div>

      <div className="detail-main-card">
        {/* Header */}
        <div className="detail-card-header">
          <div className="detail-icon-wrapper">
            <Package size={32} />
          </div>
          <div className="detail-title-section">
            <h1>Protocolo RT-{returnDetail.id}</h1>
            <p>
              {returnDetail.type === 'return' ? 'Devolução' : 'Troca'} • Criado em {formatDate(returnDetail.created_at)}
            </p>
          </div>
        </div>

        {/* Informações Gerais */}
        <div className="info-grid">
          <div className="info-card">
            <span className="info-label">Cliente</span>
            <p className="info-value">{returnDetail.customer_name || 'Cliente não informado'}</p>
          </div>
          <div className="info-card">
            <span className="info-label">Valor Total</span>
            <p className="info-value primary">{formatCurrency(returnDetail.total_amount || 0)}</p>
          </div>
        </div>

        {/* Itens */}
        <div className="items-section">
          <h3 className="section-title">Itens da Devolução</h3>
          <div className="items-list">
            {returnDetail.items && returnDetail.items.length > 0 ? (
              returnDetail.items.map((item, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <p className="item-product-name">{item.product_name || `Produto #${item.product_id}`}</p>
                    <p className="item-details">
                      Quantidade: {item.quantity} • Preço Unit.: {formatCurrency(item.unit_price)} • Total: {formatCurrency(item.unit_price * item.quantity)}
                    </p>
                    {item.reason && (
                      <p className="item-reason">{item.reason}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-items">Nenhum item encontrado</p>
            )}
          </div>
        </div>

        {/* Observações */}
        {returnDetail.observation && (
          <div className="observations-section">
            <h3 className="section-title">Observações</h3>
            <div className="observations-card">
              <p className="observations-text">{returnDetail.observation}</p>
            </div>
          </div>
        )}

        {/* Ações */}
        {canApprove && (returnDetail.status === 'pending' || returnDetail.status === 'collected' || returnDetail.status === 'in_inspection') && (
          <div className="actions-section">
            <button
              onClick={handleApproveClick}
              className="btn-approve"
            >
              <FileCheck size={18} /> Aprovar Devolução
            </button>
            {returnDetail.status === 'pending' && (
              <button
                onClick={handleRejectClick}
                className="btn-reject"
              >
                <Ban size={18} /> Rejeitar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Aprovação */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApproveConfirm}
        title="Aprovar Devolução"
        message={
          <div>
            <p style={{ marginBottom: '1rem' }}>
              Deseja realmente aprovar esta devolução?
            </p>
            <div style={{ 
              background: 'var(--bg-secondary, #f9fafb)', 
              padding: '1rem', 
              borderRadius: '1rem',
              fontSize: '0.875rem'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>Ao aprovar, será realizado:</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>Os produtos retornarão ao estoque</li>
                <li>O histórico de movimentação será registrado</li>
                <li>Um crédito será gerado para o cliente</li>
              </ul>
            </div>
          </div>
        }
        type="success"
        confirmText="Aprovar"
        cancelText="Cancelar"
        showCancel={true}
      />

      {/* Modal de Confirmação de Rejeição */}
      <ConfirmModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        onConfirm={handleRejectConfirm}
        title="Rejeitar Devolução"
        message={
          <div>
            <p style={{ marginBottom: '1rem' }}>
              Informe o motivo da rejeição:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Digite o motivo da rejeição..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '0.75rem',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color, #e5e7eb)',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
        }
        type="danger"
        confirmText="Rejeitar"
        cancelText="Cancelar"
        showCancel={true}
      />
    </div>
  );
};

export default ReturnsDetailPage;
